import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import {
    PushNotifications,
    type ActionPerformed,
    type PushNotificationSchema,
    type Token,
} from '@capacitor/push-notifications';
import type {
    PushHandlerMap,
    PushNotificationPayload,
    PushPlatform,
} from '../../types/push';

const PUSH_TOKEN_STORAGE_KEY = 'push_token_cached';
const PUSH_ENABLED = import.meta.env.VITE_PUSH_ENABLED === 'true';
export type PushPermissionState = 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied';
const ANDROID_PUSH_CHANNEL_ID = 'marketplace_general';

interface PushListeners {
    onRegistration: (token: string) => void | Promise<void>;
    onRegistrationError: (error: unknown) => void;
    onForegroundNotification: (payload: PushNotificationPayload) => void | Promise<void>;
    onNotificationAction: (payload: PushNotificationPayload) => void | Promise<void>;
}

let registeredListeners: PluginListenerHandle[] = [];
let listenersInitialized = false;

function safeStorageGet(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function safeStorageSet(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Ignorar error de storage en entornos restringidos.
    }
}

function safeStorageRemove(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch {
        // Ignorar error de storage en entornos restringidos.
    }
}

function parseMeta(rawMeta: unknown): Record<string, unknown> {
    if (typeof rawMeta === 'string' && rawMeta.trim() !== '') {
        try {
            const parsed = JSON.parse(rawMeta) as unknown;
            if (parsed && typeof parsed === 'object') {
                return parsed as Record<string, unknown>;
            }
        } catch {
            return {};
        }
    }

    if (rawMeta && typeof rawMeta === 'object') {
        return rawMeta as Record<string, unknown>;
    }

    return {};
}

function normalizePushPayload(
    notification: PushNotificationSchema | ActionPerformed['notification']
): PushNotificationPayload {
    const data = notification.data ?? {};
    const type = typeof data.type === 'string' ? data.type : 'unknown_push';
    const deepLink = typeof data.deep_link === 'string' ? data.deep_link : undefined;

    return {
        title: notification.title ?? '',
        body: notification.body ?? '',
        type,
        deep_link: deepLink,
        meta: parseMeta(data.meta),
        rawData: data,
    };
}

export function isPushNativeSupported(): boolean {
    if (!PUSH_ENABLED || !Capacitor.isNativePlatform()) {
        return false;
    }

    const platform = Capacitor.getPlatform();
    return platform === 'android' || platform === 'ios';
}

export function getPushPlatform(): PushPlatform | null {
    if (!isPushNativeSupported()) {
        return null;
    }

    return Capacitor.getPlatform() as PushPlatform;
}

export async function ensurePushPermissions(): Promise<'granted' | 'denied'> {
    if (!isPushNativeSupported()) {
        return 'denied';
    }

    const current = await PushNotifications.checkPermissions();
    if (current.receive === 'granted') {
        return 'granted';
    }

    if (current.receive === 'denied') {
        return 'denied';
    }

    const requested = await PushNotifications.requestPermissions();
    return requested.receive === 'granted' ? 'granted' : 'denied';
}

export async function getPushPermissionState(): Promise<PushPermissionState | 'unavailable'> {
    if (!isPushNativeSupported()) {
        return 'unavailable';
    }

    const status = await PushNotifications.checkPermissions();
    return status.receive;
}

/**
 * Solicita permiso push de forma explícita (desde ajustes).
 */
export async function requestPushPermissionsFromSettings(): Promise<'granted' | 'denied'> {
    if (!isPushNativeSupported()) {
        return 'denied';
    }

    const requested = await PushNotifications.requestPermissions();
    return requested.receive === 'granted' ? 'granted' : 'denied';
}

export async function registerPushClient(): Promise<void> {
    if (!isPushNativeSupported()) {
        return;
    }

    if (Capacitor.getPlatform() === 'android') {
        await ensureAndroidNotificationChannel();
    }

    await PushNotifications.register();
}

async function ensureAndroidNotificationChannel(): Promise<void> {
    try {
        // Re-crear canal para evitar configuración vieja (importancia/sonido) en instalaciones previas.
        await PushNotifications.deleteChannel({ id: ANDROID_PUSH_CHANNEL_ID }).catch(() => undefined);

        await PushNotifications.createChannel({
            id: ANDROID_PUSH_CHANNEL_ID,
            name: 'Troquero',
            description: 'Notificaciones generales',
            importance: 5,
            visibility: 1,
            sound: 'default',
            vibration: true,
            lights: true,
        });
    } catch (error) {
        console.warn('[Push] No se pudo crear canal de Android:', error);
    }
}

export async function initializePushListeners(listeners: PushListeners): Promise<void> {
    if (!isPushNativeSupported() || listenersInitialized) {
        return;
    }

    const handles: PluginListenerHandle[] = [];

    handles.push(await PushNotifications.addListener('registration', (token: Token) => {
        safeStorageSet(PUSH_TOKEN_STORAGE_KEY, token.value);
        void listeners.onRegistration(token.value);
    }));

    handles.push(await PushNotifications.addListener('registrationError', (error) => {
        listeners.onRegistrationError(error);
    }));

    handles.push(await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        void listeners.onForegroundNotification(normalizePushPayload(notification));
    }));

    handles.push(await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        void listeners.onNotificationAction(normalizePushPayload(action.notification));
    }));

    registeredListeners = handles;
    listenersInitialized = true;
}

export async function teardownPushListeners(): Promise<void> {
    if (!listenersInitialized) {
        return;
    }

    const handles = registeredListeners;
    registeredListeners = [];
    listenersInitialized = false;

    await Promise.all(handles.map((handle) => handle.remove()));
}

export function getCachedPushToken(): string | null {
    return safeStorageGet(PUSH_TOKEN_STORAGE_KEY);
}

export function clearCachedPushToken(): void {
    safeStorageRemove(PUSH_TOKEN_STORAGE_KEY);
}

/**
 * Despacha un payload al handler por tipo.
 */
export async function dispatchPushByType(
    payload: PushNotificationPayload,
    handlers: PushHandlerMap,
    onFallback: (payload: PushNotificationPayload) => void | Promise<void>
): Promise<void> {
    const handler = handlers[payload.type];

    if (handler) {
        await handler(payload);
        return;
    }

    await onFallback(payload);
}
